import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  FolderLock,
  FolderPlus,
  Upload,
  Download,
  Trash2,
  FileText,
  Lock as LockIcon,
} from "lucide-react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, API } from "../lib/api";

export default function Documents() {
  const { viewOnly } = useAuth();
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null); // null = root
  const [docs, setDocs] = useState([]);
  const [newFolderName, setNewFolderName] = useState("");
  const [uploadForm, setUploadForm] = useState({
    title: "",
    category: "other",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [f, d] = await Promise.all([
        api.get("/vault/folders"),
        api.get(
          `/vault/documents${activeFolder ? `?folder_id=${activeFolder}` : "?folder_id=root"}`,
        ),
      ]);
      setFolders(f.data.folders || []);
      setDocs(d.data.documents || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, [activeFolder]);

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await api.post("/vault/folders", {
        name: newFolderName,
        parent_id: activeFolder || null,
      });
      setNewFolderName("");
      toast.success("Folder created");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteFolder = async (id) => {
    if (!window.confirm("Delete folder and all its documents?")) return;
    try {
      await api.delete(`/vault/folders/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const upload = async () => {
    if (!file || !uploadForm.title) {
      toast.error("File and title required");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", uploadForm.title);
      fd.append("category", uploadForm.category);
      if (activeFolder) fd.append("folder_id", activeFolder);
      await api.post("/vault/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadForm({ title: "", category: "other" });
      setFile(null);
      toast.success("Uploaded & encrypted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/vault/documents/${doc.id}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: doc.mime_type || "application/octet-stream",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.title;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      await api.delete(`/vault/documents/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const folderChildren = folders.filter(
    (f) => (f.parent_id || null) === (activeFolder || null),
  );

  return (
    <DashboardLayout>
      <div className="mb-8" data-testid="documents-page">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-display font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <FolderLock className="w-7 h-7 text-emerald-600" /> Document Vault
          </h1>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
            <LockIcon className="w-4 h-4" /> AES-256 encrypted at rest. Shared
            documents go through Family Sharing.
          </p>
        </div>

        {/* Breadcrumb */}
        <div className="mb-4 text-sm flex items-center gap-2">
          <button
            onClick={() => setActiveFolder(null)}
            className={
              activeFolder ? "text-emerald-600 hover:underline" : "font-medium"
            }
            data-testid="folder-root"
          >
            Root
          </button>
          {activeFolder && (
            <>
              <span className="text-slate-400">/</span>
              <span className="font-medium">
                {folders.find((f) => f.id === activeFolder)?.name || "—"}
              </span>
            </>
          )}
        </div>

        {/* Folder strip */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold">Folders</h2>
            {!viewOnly && (
              <div className="flex gap-2">
                <input
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="New folder…"
                  className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm"
                  data-testid="new-folder-input"
                />
                <button
                  onClick={createFolder}
                  className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm flex items-center gap-1"
                  data-testid="create-folder-btn"
                >
                  <FolderPlus className="w-4 h-4" /> Create
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {folderChildren.length === 0 && (
              <span className="text-sm text-slate-500">
                No folders here yet.
              </span>
            )}
            {folderChildren.map((f) => (
              <div
                key={f.id}
                className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm"
              >
                <button
                  onClick={() => setActiveFolder(f.id)}
                  className="flex items-center gap-2 hover:text-emerald-600"
                  data-testid={`folder-${f.id}`}
                >
                  <FolderLock className="w-4 h-4" /> {f.name}
                </button>
                <button
                  onClick={() => deleteFolder(f.id)}
                  className="opacity-0 group-hover:opacity-100 text-rose-500"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Upload */}
        {!viewOnly && (
        <div
          className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 mb-6"
          data-testid="upload-card"
        >
          <h2 className="text-base font-semibold mb-3">Upload Document</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <input
              type="text"
              placeholder="Title"
              value={uploadForm.title}
              onChange={(e) =>
                setUploadForm({ ...uploadForm, title: e.target.value })
              }
              className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm"
              data-testid="doc-title"
            />
            <select
              value={uploadForm.category}
              onChange={(e) =>
                setUploadForm({ ...uploadForm, category: e.target.value })
              }
              className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm"
              data-testid="doc-category"
            >
              {[
                "insurance",
                "property",
                "will",
                "nomination",
                "tax",
                "investment_statement",
                "other",
              ].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="
              px-3 py-2 rounded-lg border border-slate-300
             dark:border-slate-700 bg-white dark:bg-slate-800 text-sm

              file:mr-4
              file:px-4
              file:py-2
              file:rounded-md
              file:border-0
            file:bg-emerald-50
             file:text-emerald-700
              file:font-medium
             hover:file:bg-emerald-100
            "
              data-testid="doc-file-input"
            />
            <button
              onClick={upload}
              disabled={busy}
              className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm flex items-center justify-center gap-1 disabled:opacity-50"
              data-testid="upload-btn"
            >
              <Upload className="w-4 h-4" /> {busy ? "Uploading…" : "Upload"}
            </button>
          </div>
        </div>
        )}

        {/* Documents */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800">
            <h2 className="text-base font-semibold">
              Documents in{" "}
              {activeFolder
                ? folders.find((f) => f.id === activeFolder)?.name
                : "Root"}
            </h2>
          </div>
          {docs.length === 0 ? (
            <div
              className="p-8 text-center text-slate-500"
              data-testid="docs-empty"
            >
              No documents here.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-800/50">
                <tr>
                  <th className="text-left px-5 py-2">Title</th>
                  <th>Category</th>
                  <th>Size</th>
                  <th>Uploaded</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {docs.map((d) => (
                  <tr
                    key={d.id}
                    className="border-t border-slate-200 dark:border-slate-800"
                    data-testid={`doc-row-${d.id}`}
                  >
                    <td className="px-5 py-3 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-emerald-600" />{" "}
                      {d.title}
                    </td>
                    <td className="text-center">{d.category}</td>
                    <td className="text-center">
                      {Math.round((d.size_bytes || 0) / 1024)} KB
                    </td>
                    <td className="text-center">
                      {(d.created_at || "").slice(0, 10)}
                    </td>
                    <td className="text-right px-5">
                      <button
                        onClick={() => downloadDoc(d)}
                        className="text-emerald-600 hover:underline mr-3 inline-flex items-center gap-1"
                      >
                        <Download className="w-3 h-3" /> Download
                      </button>
                      <button
                        onClick={() => deleteDoc(d.id)}
                        className="text-rose-500"
                      >
                        <Trash2 className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
